/**
 * AnalyticsFilterBar
 * Shared filter bar with configurable toggle slots for analytics views.
 * Each view shows only the toggles relevant to it.
 */

import type { AnalysisLevel, AnalyticsFilter } from '@parlay-picker/shared/analyticsScopes';
import { ANALYSIS_LEVELS, ANALYTICS_FILTERS } from '@parlay-picker/shared/analyticsScopes';
import type { ReactElement } from 'react';

// =============================================================================
// Toggle Group (reusable pill-style selector)
// =============================================================================

interface ToggleGroupProps<T extends string> {
  options: { value: T; label: string; description?: string }[];
  selected: T;
  onChange: (value: T) => void;
}

export function ToggleGroup<T extends string>({
  options,
  selected,
  onChange,
}: ToggleGroupProps<T>): ReactElement {
  return (
    <div className="flex rounded-lg bg-theme-surface/50 p-0.5">
      {options.map((opt) => (
        <button
          type="button"
          key={opt.value}
          onClick={() => onChange(opt.value)}
          title={opt.description}
          className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
            selected === opt.value
              ? 'bg-theme-primary-fill text-theme-on-primary-fill'
              : 'text-theme-text-secondary hover:text-gray-200'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// =============================================================================
// Filter Bar
// =============================================================================

/** Filter options for the analytics filter bar */
type FilterOption = 'all_tracked' | 'real_bets';

const FILTER_OPTIONS: { value: FilterOption; label: string; description: string }[] = [
  {
    value: 'all_tracked',
    label: ANALYTICS_FILTERS.all_tracked.label,
    description: ANALYTICS_FILTERS.all_tracked.description,
  },
  {
    value: 'real_bets',
    label: ANALYTICS_FILTERS.real_bets.label,
    description: ANALYTICS_FILTERS.real_bets.description,
  },
];

const LEVEL_OPTIONS: { value: AnalysisLevel; label: string }[] = [
  { value: 'legs', label: ANALYSIS_LEVELS.legs.label },
  { value: 'parlays', label: ANALYSIS_LEVELS.parlays.label },
];

interface AnalyticsFilterBarProps {
  /** Show All Tracked / Real Bets filter toggle */
  showFilter?: boolean;
  filter?: AnalyticsFilter;
  onFilterChange?: (filter: AnalyticsFilter) => void;

  /** Show Legs / Parlays level toggle */
  showLevel?: boolean;
  level?: AnalysisLevel;
  onLevelChange?: (level: AnalysisLevel) => void;
}

export function AnalyticsFilterBar({
  showFilter,
  filter,
  onFilterChange,
  showLevel,
  level,
  onLevelChange,
}: AnalyticsFilterBarProps): ReactElement | null {
  if (!showFilter && !showLevel) return null;

  return (
    <div className="flex flex-wrap items-center gap-3 mb-4">
      {showFilter && filter && onFilterChange && (
        <ToggleGroup<FilterOption>
          options={FILTER_OPTIONS}
          selected={filter as FilterOption}
          onChange={onFilterChange}
        />
      )}

      {showFilter && showLevel && <div className="w-px h-5 bg-theme-surface-hover" />}

      {showLevel && level && onLevelChange && (
        <ToggleGroup<AnalysisLevel>
          options={LEVEL_OPTIONS}
          selected={level}
          onChange={onLevelChange}
        />
      )}
    </div>
  );
}
